import { createStore } from "zustand/vanilla";
import { devtools } from "zustand/middleware";
import { PortalAuthService } from "../../services/portal/PortalAuthService";
import { extractApiError } from "../../utils/extractApiError";
import { DEFAULT_OTP_CHANNELS } from "../../utils/resolveOtpChannels";
import { isValidOtpDestination, otpDestinationErrorKey, } from "../../types/otpChannel";
export const PORTAL_ERROR_KEYS = {
    sendFailed: "errorSendFailed",
    codeLength: "errorCodeLength",
    codeRejected: "errorCodeRejected",
    sessionExpired: "sessionExpired",
};
function retryAfterSeconds(e) {
    const err = e;
    if (err?.response?.status !== 429)
        return null;
    const seconds = Number(err.response.headers?.["retry-after"]);
    return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60;
}
const FALLBACK_COUNTRY_CODE = "+91";
export function createPortalStore(options = {}) {
    const channels = options.otpChannels?.length
        ? options.otpChannels
        : [...DEFAULT_OTP_CHANNELS];
    const destinationOf = (state) => state.channel === "email"
        ? { channel: "email", email: state.email.trim() }
        : {
            channel: "phone",
            countryCode: state.countryCode,
            phoneNumber: state.phoneNumber,
        };
    return createStore()(devtools((set, get) => ({
        view: "phone",
        channels,
        channel: channels[0],
        countryCode: options.defaultCountryCode ?? FALLBACK_COUNTRY_CODE,
        phoneNumber: "",
        email: "",
        otpCode: "",
        requestId: null,
        otpSending: false,
        otpVerifying: false,
        otpError: null,
        rateLimitedUntil: null,
        setCountryCode: (code) => set({ countryCode: code }),
        setPhoneNumber: (phone) => set({ phoneNumber: phone }),
        setEmail: (email) => set({ email }),
        setOtpCode: (code) => set({ otpCode: code }),
        setChannel: (channel) => {
            if (get().channel === channel)
                return;
            set({
                channel,
                view: "phone",
                otpCode: "",
                requestId: null,
                otpError: null,
            });
        },
        changeDestination: () => set({
            view: "phone",
            otpCode: "",
            requestId: null,
            otpError: null,
        }),
        requestOtp: async () => {
            const state = get();
            if (state.rateLimitedUntil && state.rateLimitedUntil > Date.now()) {
                return;
            }
            if (!isValidOtpDestination(state.channel, {
                countryCode: state.countryCode,
                phoneNumber: state.phoneNumber,
                email: state.email,
            })) {
                set({ otpError: otpDestinationErrorKey(state.channel) });
                return;
            }
            set({ otpSending: true, otpError: null });
            try {
                await PortalAuthService.requestOtp(destinationOf(state));
                set({ view: "code", otpCode: "" });
            }
            catch (e) {
                const retryAfter = retryAfterSeconds(e);
                set({
                    rateLimitedUntil: retryAfter
                        ? Date.now() + retryAfter * 1000
                        : null,
                    otpError: extractApiError(e, PORTAL_ERROR_KEYS.sendFailed),
                });
            }
            finally {
                set({ otpSending: false });
            }
        },
        verifyOtp: async () => {
            const state = get();
            if (!state.otpCode || state.otpCode.length !== 6) {
                set({ otpError: PORTAL_ERROR_KEYS.codeLength });
                return;
            }
            set({ otpVerifying: true, otpError: null });
            try {
                await PortalAuthService.verifyOtp({
                    ...destinationOf(state),
                    otpCode: state.otpCode,
                });
                set({ view: "dashboard", otpCode: "", otpError: null });
            }
            catch (e) {
                const retryAfter = retryAfterSeconds(e);
                set({
                    rateLimitedUntil: retryAfter
                        ? Date.now() + retryAfter * 1000
                        : null,
                    otpError: extractApiError(e, PORTAL_ERROR_KEYS.codeRejected),
                });
            }
            finally {
                set({ otpVerifying: false });
            }
        },
        signOut: () => {
            set({
                view: "phone",
                otpCode: "",
                requestId: null,
                otpError: null,
                patientScope: null,
                tab: "UPCOMING",
                rescheduleTarget: null,
            });
        },
        handleSessionExpired: () => set({
            view: "phone",
            otpCode: "",
            requestId: null,
            patientScope: null,
            rescheduleTarget: null,
            otpError: PORTAL_ERROR_KEYS.sessionExpired,
        }),
        patientScope: null,
        tab: "UPCOMING",
        setPatientScope: (scope) => set({ patientScope: scope }),
        setTab: (tab) => set({ tab }),
        startBooking: () => set({ view: "booking" }),
        exitBooking: () => set({ view: "dashboard" }),
        rescheduleTarget: null,
        startReschedule: (appointment) => set({ view: "reschedule", rescheduleTarget: appointment }),
        cancelReschedule: () => set({ view: "dashboard", rescheduleTarget: null }),
    }), {
        name: "MedosPatientPortal",
        enabled: process.env.NODE_ENV !== "production",
    }));
}
